// client/src/pages/canvas/AnalyticsCard.tsx
//
// Chat-inline analytics card. Switches on cardType and renders one of
// 5 compact views using recharts. Clicking "View Details →" opens the
// existing SidebarPanel with a new 'analytics' type for the full view.
//
// In `bar_list` mode (e.g. "Top ads by ROAS"), each individual ad row
// is clickable and opens the AdDetailSidebar with that ad's KPIs and
// daily trend chart — see canvas-chat.tsx for the click wiring.

import React, { useState } from "react";
import { LineChart, Line, ResponsiveContainer, Tooltip } from "recharts";
import type { AnalyticsToolResult, KpiTile, RenderCardData } from "../../../../server/services/analytics/types";
import { useCanvasStore } from "./canvas-store";
import { AdDetailSidebar } from "../../components/canvas/AdDetailSidebar";
import type { AdCardData } from "./canvas-types";

interface AnalyticsCardProps {
  result: AnalyticsToolResult;
  toolName: string;
}

// Convert a bar_list row into an AdCardData so the AdDetailSidebar can render it.
// The sidebar fetches /api/meta/insights/ad/:adId — using r.name as the id lets
// the mock-fallback path match on mock_ads.name. For synthesized names that
// don't match a real ad row, the sidebar falls through to "no daily data" but
// still shows the row's KPIs from the props.
function rowToAdCardData(row: { adId: string; name: string; thumbnailUrl?: string; primaryValue: number; primaryLabel: string; kpis: KpiTile[] }): AdCardData {
  // Pick metrics from the row's KPIs by label match (order isn't guaranteed)
  const metricByLabel = (label: string): number | undefined => {
    const m = row.kpis.find((k) => k.label.toLowerCase().includes(label.toLowerCase()));
    if (!m) return undefined;
    const num = parseFloat(String(m.value).replace(/[^0-9.\-]/g, ""));
    return isNaN(num) ? undefined : num;
  };
  return {
    id: row.adId || row.name,
    name: row.name,
    thumbnail_url: row.thumbnailUrl,
    metrics: {
      roas: row.primaryLabel.toLowerCase().includes("roas") ? row.primaryValue : metricByLabel("roas"),
      spend: metricByLabel("spend"),
      ctr: metricByLabel("ctr"),
      cpc: metricByLabel("cpc"),
      conversions: metricByLabel("purch"),
    },
  };
}

function KpiPill({ kpi }: { kpi: KpiTile }) {
  const arrow = kpi.direction === "up" ? "↑" : kpi.direction === "down" ? "↓" : "";
  const color = kpi.direction === "up" ? "#16A34A" : kpi.direction === "down" ? "#DC2626" : "#6B7280";
  return (
    <div style={{ padding: "8px 10px", background: "#F9FAFB", borderRadius: 8, minWidth: 84 }}>
      <div style={{ fontSize: 10, textTransform: "uppercase", color: "#6B7280", letterSpacing: 0.3 }}>{kpi.label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: "#111827", marginTop: 2 }}>{kpi.value}</div>
      {kpi.delta && (
        <div style={{ fontSize: 11, color, marginTop: 2 }}>{arrow} {kpi.delta}</div>
      )}
    </div>
  );
}

function renderCardData(data: RenderCardData, onRowClick?: (row: { adId: string; name: string; thumbnailUrl?: string; primaryValue: number; primaryLabel: string; kpis: KpiTile[] }) => void) {
  switch (data.cardType) {
    case "kpi_delta": {
      const sparkData = (data.sparkline ?? []).map((v, i) => ({ i, v }));
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div>
            <div style={{ fontSize: 12, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.3 }}>{data.metric}</div>
            <div style={{ fontSize: 32, fontWeight: 700, color: "#111827", lineHeight: 1.1 }}>{data.current}</div>
          </div>
          {sparkData.length > 0 && (
            <div style={{ flex: 1, height: 36 }}>
              <ResponsiveContainer><LineChart data={sparkData}><Line type="monotone" dataKey="v" stroke="#6B7FE6" strokeWidth={2} dot={false} /></LineChart></ResponsiveContainer>
            </div>
          )}
        </div>
      );
    }
    case "kpi_strip":
      return <div style={{ display: "flex", gap: 8, overflowX: "auto" }}>{data.kpis.map((k, i) => <KpiPill key={i} kpi={k} />)}</div>;
    case "sparkline": {
      const points = data.points.map((p) => ({ ...p }));
      return (
        <div style={{ height: 72 }}>
          <ResponsiveContainer>
            <LineChart data={points}>
              <Line type="monotone" dataKey="v" stroke="#6B7FE6" strokeWidth={2} dot={false} />
              <Tooltip contentStyle={{ fontSize: 11, padding: 4 }} labelFormatter={(label) => String(label)} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      );
    }
    case "bar_list":
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {data.rows.map((r) => {
            const clickable = !!onRowClick;
            return (
              <button
                key={r.adId}
                type="button"
                onClick={clickable ? () => onRowClick!(r) : undefined}
                disabled={!clickable}
                data-testid={`analytics-bar-row-${r.adId}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: 8,
                  background: "#F9FAFB",
                  borderRadius: 8,
                  border: "1px solid transparent",
                  textAlign: "left",
                  cursor: clickable ? "pointer" : "default",
                  transition: "background 0.15s, border-color 0.15s",
                  width: "100%",
                }}
                onMouseEnter={(e) => {
                  if (clickable) {
                    (e.currentTarget as HTMLButtonElement).style.background = "#F3F4F6";
                    (e.currentTarget as HTMLButtonElement).style.borderColor = "#E5E7EB";
                  }
                }}
                onMouseLeave={(e) => {
                  if (clickable) {
                    (e.currentTarget as HTMLButtonElement).style.background = "#F9FAFB";
                    (e.currentTarget as HTMLButtonElement).style.borderColor = "transparent";
                  }
                }}
              >
                {r.thumbnailUrl && <img src={r.thumbnailUrl} alt="" style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4, flexShrink: 0 }} onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</div>
                  <div style={{ fontSize: 10, color: "#6B7280", marginTop: 2 }}>{r.primaryLabel}: <strong>{r.primaryValue.toFixed(2)}</strong> · {r.kpis.map((k) => `${k.label} ${k.value}`).join(" · ")}</div>
                </div>
                {clickable && (
                  <span style={{ fontSize: 10, color: "#9CA3AF", flexShrink: 0 }}>›</span>
                )}
              </button>
            );
          })}
        </div>
      );
    case "comparison":
      return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {[data.periodA, data.periodB].map((p, i) => (
            <div key={i} style={{ padding: 10, background: "#F9FAFB", borderRadius: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#374151", marginBottom: 6 }}>{p.label}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {p.kpis.map((k, j) => <KpiPill key={j} kpi={k} />)}
              </div>
            </div>
          ))}
        </div>
      );
  }
}

export function AnalyticsCard({ result, toolName }: AnalyticsCardProps) {
  const openSidebarPanel = useCanvasStore((s) => s.openSidebarPanel);
  // brandId for the per-ad "Explain performance" feature in the sidebar.
  // Falls back to undefined when no brand is active — sidebar hides the button.
  const activeBrandId = useCanvasStore((s) => s.activeBrandId);
  const { renderCard, heartbeatLinks } = result;
  const [selectedAd, setSelectedAd] = useState<AdCardData | null>(null);

  const handleView = () => {
    openSidebarPanel({
      type: "analytics",
      title: renderCard.title,
      analyticsData: { toolName, analysis: result.analysis, renderCard: result.renderCard, heartbeatLinks: result.heartbeatLinks },
    });
  };

  // Per-ad-row click → open AdDetailSidebar with that specific ad's data + chart.
  // Only fires for bar_list cards; other card types pass undefined and rows
  // render as static (non-clickable) to preserve previous behavior.
  const handleRowClick =
    renderCard.data.cardType === "bar_list"
      ? (row: { adId: string; name: string; thumbnailUrl?: string; primaryValue: number; primaryLabel: string; kpis: KpiTile[] }) => {
          setSelectedAd(rowToAdCardData(row));
        }
      : undefined;

  // bar_list rows are individually clickable → "View Details" aggregate is
  // redundant (it just shows topN totals which aren't actionable when each
  // row already opens its own per-ad sidebar with KPIs + chart + Explain).
  // Other card types (kpi_delta, kpi_strip, sparkline, comparison) keep the
  // link because they don't have row-level entry points.
  const showViewDetails = renderCard.data.cardType !== "bar_list";

  return (
    <>
      <div
        data-testid={`analytics-card-${renderCard.data.cardType}`}
        style={{ padding: 12, background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 12, marginTop: 8 }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{renderCard.title}</div>
            <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>{renderCard.timeframeLabel}</div>
          </div>
          {showViewDetails && (
            <button
              onClick={handleView}
              style={{ fontSize: 11, color: "#6B7FE6", background: "transparent", border: "none", cursor: "pointer", padding: "4px 0" }}
            >
              View Details →
            </button>
          )}
        </div>

        {renderCardData(renderCard.data, handleRowClick)}

        {heartbeatLinks.length > 0 && (
          <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid #F3F4F6", fontSize: 10, color: "#9CA3AF" }}>
            Based on {heartbeatLinks.length} Heartbeat {heartbeatLinks.length === 1 ? "chunk" : "chunks"} — {heartbeatLinks[0].summary.slice(0, 60)}{heartbeatLinks[0].summary.length > 60 ? "…" : ""}
          </div>
        )}
      </div>
      {selectedAd && (
        <AdDetailSidebar
          ad={selectedAd}
          onClose={() => setSelectedAd(null)}
          brandId={activeBrandId ?? undefined}
        />
      )}
    </>
  );
}
